import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import {
  applicationStore,
  betStore,
  convertAmount,
  DefaultQueryParams,
  GameBackside,
  gameConfigStore,
  GameControllers,
  getAmountWithCurrency,
  iframeService,
  loadingStore,
  MobileGameHolder,
  MobileGameStructure,
  MobileGameWr,
  MobileStructureMiddle,
  parseQuery,
  platformMessageStore,
  PlayForRealPopup,
  RightSideHolder,
  tableBetHistoryStore,
  TableFooter,
  TableMobileHeader,
  userCountStore,
  userInfoStore,
  useSoundWatcher,
  useTranslation,
} from '@pascal/pascal-core';

import { GAME_CONTROLLER_TYPES } from '@pascal/pascal-core/shared/core/enums/gameControllerTypes';
import { GameControllerTypes } from '@pascal/pascal-core/table-line/t_types/enums/GameControllerTypes';

import {
  AdditionalErrorMessages,
  AdditionalNotificationByResponseCode,
  AdditionalResponseCode,
} from '../enums/notifications';
import { MobileGameMain } from '../game';
import { BetHistoryWr } from './betHistory';
import LiveBets from '../components/liveBets';
import { HelpPopup } from '../components/helpPopup';
import { SettingsContainer } from './settingsContainer';
import { globalTranslationKeys } from '../enums/translationKeys';
import { GAME_CONTROLLER_TYPES_YET } from '../enums/constants';
import logo from '../assets/images/logo.svg';
import MobileResultHistoryContainer from './mobileResultHistoryContainer';
import { gameMainDrawer } from '../game/drawers/gameDrawer/gameMainDrawer';
import { setScreenResolution } from '../helpers/getScreenResolution';
import { CurrentRoundResults } from './currentRoundResults';
import { useVisibilityChange } from '../hooks/useVisibilityChange';
import { totalBetStore } from '../store/totalBetStore';
import demoLogo from '../assets/images/demo-logo.png';

const gameControllerComponents = {
  [GAME_CONTROLLER_TYPES_YET.BETS]: <LiveBets />,
  [GAME_CONTROLLER_TYPES_YET.RESULT_HISTORY]: <MobileResultHistoryContainer />,
  [GameControllerTypes.Help]: <HelpPopup />,
  [GameControllerTypes.History]: <BetHistoryWr />,
  [GameControllerTypes.Settings]: <SettingsContainer />,
  [GameControllerTypes.Results]: <CurrentRoundResults />,
};

// const secondNumber = 1;

export const MobileGame = observer(() => {
  useSoundWatcher();
  useVisibilityChange();

  const ref = useRef();
  const t = useTranslation();
  const { search } = useLocation();
  const { usersCount } = userCountStore;
  const { isDemo } = applicationStore;
  const { currencySymbolOrCode } = userInfoStore;
  const { totalBet } = totalBetStore;
  const { repliedBetData } = betStore;

  const {
    limits: { minBet, maxBet },
  } = gameConfigStore;
  const { [DefaultQueryParams.UserId]: userId } = parseQuery(search);
  const [gameControllerType, setGameControllerType] = useState('');

  const { isOpenBetHistorySection, setIsOpenBetHistorySection } = platformMessageStore;
  const { selectedHistoryRow, setSelectedHistoryRow } = tableBetHistoryStore;

  const GameControllerComponent = useMemo(() => {
    const component = gameControllerComponents[gameControllerType];
    if (component) {
      return component;
    }
    return null;
  }, [gameControllerType]);

  useEffect(() => {
    if (ref.current) {
      gameMainDrawer.execute(ref.current).then(() => {
        loadingStore.updateLoading({ isPixiLoaded: true });
      });

      setScreenResolution(ref.current);
    }
  }, [ref]);

  useEffect(() => {
    if (!gameControllerType) {
      setIsOpenBetHistorySection(false);
    }
    if (isOpenBetHistorySection) {
      setGameControllerType(GAME_CONTROLLER_TYPES.HISTORY);
    }
  }, [isOpenBetHistorySection, gameControllerType]);

  const navigationItems = useMemo(() => {
    return [
      {
        icon: 'icon-home1',
        name: t(globalTranslationKeys.Home),
        onClick: () => iframeService.exitGame(),
      },
      {
        icon: 'icon-results', // icon-double-arrow-right
        name: t('RESULTS'),
        onClick: () => setGameControllerType(GAME_CONTROLLER_TYPES.RESULTS),
      },
      {
        icon: 'icon-result-history',
        name: t('RESULT_HISTORY'),
        onClick: () => setGameControllerType(GAME_CONTROLLER_TYPES_YET.RESULT_HISTORY),
      },
      {
        icon: 'icon-bets',
        name: t(globalTranslationKeys.Bets),
        onClick: () => setGameControllerType(GAME_CONTROLLER_TYPES_YET.BETS),
      },
      {
        icon: 'icon-history',
        name: t(globalTranslationKeys.History),
        isDisabled: !userId || isDemo,
        onClick: () => setGameControllerType(GAME_CONTROLLER_TYPES.HISTORY),
      },
      {
        icon: 'icon-help',
        name: t(globalTranslationKeys.Help),
        onClick: () => setGameControllerType(GAME_CONTROLLER_TYPES.HELP),
      },
      {
        icon: 'icon-settings',
        name: t(globalTranslationKeys.Settings),
        onClick: () => setGameControllerType(GAME_CONTROLLER_TYPES.SETTINGS),
      },
    ];
  }, [isDemo, t, userId]);

  useEffect(() => {
    const el = document.createElement('span');
    const parent = document.querySelector('.m-user-count-holder');
    el.classList.add('m-user-count-txt');
    el.innerText = usersCount || '';
    parent?.prepend(el);

    if (!usersCount) {
      parent.innerHTML = '';
    }
  }, [usersCount]);

  return (
    <MobileGameWr>
      <MobileGameStructure isShowBogLineLobby hasLobby hasGameTypeLoading>
        <TableMobileHeader hasFreeBets logoSrc={logo} isShowGameTypes />
        <MobileStructureMiddle
          haveNotification
          AdditionalResponseCode={AdditionalResponseCode}
          AdditionalErrorMessages={AdditionalErrorMessages}
          AdditionalNotificationByResponseCode={AdditionalNotificationByResponseCode}
          translationValues={{
            minBet: getAmountWithCurrency({ amount: convertAmount(minBet) }),
            maxBet: getAmountWithCurrency({ amount: convertAmount(maxBet) }),
          }}
        >
          <MobileGameHolder>
            <GameBackside refProp={ref} />
            <MobileGameMain />
          </MobileGameHolder>
          <GameControllers
            currencyCode={currencySymbolOrCode}
            controllerType={gameControllerType}
            selectedHistoryRow={selectedHistoryRow}
            setSelectedHistoryRow={setSelectedHistoryRow}
            gameControllerComponent={GameControllerComponent}
            setGameControllerType={() => setGameControllerType(null)}
          />
          <RightSideHolder navigationItems={navigationItems} />
        </MobileStructureMiddle>
        {!repliedBetData && (
          <TableFooter
            totalBet={totalBet}
            centerBlock={
              <>
                <div className='user-icon-b'>
                  <i className='icon-users1' />
                </div>
                <div className='m-user-count-holder' />
              </>
            }
          />
        )}

        <PlayForRealPopup popupLogo={demoLogo} />
      </MobileGameStructure>
    </MobileGameWr>
  );
});
